import { useEffect, useState } from 'react';
import axios from 'axios';
import { Calendar, TrendingUp, AlertCircle, ArrowRight, CalendarDays } from 'lucide-react';
import { usePortfolioScopeParams } from '@/lib/portfolioScopeParams';
import useCpiYoy from '@/hooks/useCpiYoy';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/**
 * IncomeHeatmap — Round 84k (C).
 *
 * 12-month grid that visualises projected dividend income per month
 * for the user's current holdings. Cells get brighter as projected
 * income climbs. Weak months (< 60% of avg) and strong months
 * (> 130%) are flagged so the user can see income gaps to fill.
 *
 * Pure projection — no external API calls. Powered by
 * GET /api/portfolio/income-heatmap.
 */
export default function IncomeHeatmap({ refreshKey = 0, onCalendarClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);
  // Round 96n (Jul 3 2026) — Real vs Nominal projection toggle for
  // the 12-month income heatmap. Uses monthly-compounded deflation
  // via (1 + CPI YoY)^((i+0.5)/12) so each month is discounted by the
  // midpoint of its horizon — Month 1 is barely touched (~0.35% cut)
  // and Month 12 gets the full ~4.17% cut at current CPI. Shares the
  // localStorage key with StockTable so the whole app respects one
  // user preference.
  const cpiYoy = useCpiYoy();
  const [projectionMode, setProjectionMode] = useState(() => {
    try { return localStorage.getItem('dividrip.yieldMode') || 'nominal'; } catch { return 'nominal'; }
  });
  useEffect(() => {
    try { localStorage.setItem('dividrip.yieldMode', projectionMode); } catch { /* ignore */ }
  }, [projectionMode]);
  const effectiveMode = (projectionMode === 'real' && typeof cpiYoy !== 'number') ? 'nominal' : projectionMode;
  // Round 92o++++++++N++++++++++ (Jun 2026) — scope params: active
  // portfolio + (optional) include_watchlist=false for the
  // calendar-scope toggle. Recomputed whenever the user flips the
  // toggle on this card OR on the standalone Calendar page (shared
  // store keeps both surfaces in lockstep).
  const scopeParams = usePortfolioScopeParams({ withWatchlistFlag: true });
  const scopeKey = `${scopeParams.portfolio_id || 'all'}|${scopeParams.include_watchlist === false ? 'po' : 'both'}`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/portfolio/income-heatmap`, {
      params: { months: 12, ...scopeParams },
      withCredentials: true,
    })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey, scopeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !data) return null;
  // Round 96n (Jul 3 2026) — apply real-dollar deflator when Real
  // mode is on. Each month gets its own midpoint discount factor so
  // near months are barely touched and far months get the full haircut.
  const rawMonths = data.months || [];
  const useReal = effectiveMode === 'real' && typeof cpiYoy === 'number';
  const cpiRate = useReal ? (cpiYoy / 100) : 0;
  const months = useReal
    ? rawMonths.map((m, i) => ({
        ...m,
        amount: m.amount / Math.pow(1 + cpiRate, (i + 0.5) / 12),
        _nominalAmount: m.amount,
      }))
    : rawMonths;
  if (months.length === 0) return null;
  // Recompute avg/total from the (possibly deflated) months.
  const totalAnnual = useReal
    ? months.reduce((s, m) => s + m.amount, 0)
    : data.total_annual;
  const avgAmount = useReal
    ? (months.length ? totalAnnual / months.length : 0)
    : data.avg_amount;
  const weakCount = months.filter((m) => m.is_weak && m.amount > 0).length;
  const strongCount = months.filter((m) => m.is_strong).length;

  return (
    <div
      className="rounded-xl border border-border bg-card p-4 mb-4"
      data-testid="income-heatmap"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-violet-600 dark:text-violet-400" />
          <h3 className="text-sm font-bold">12-Month Income Heatmap</h3>
          {useReal && (
            <span
              className="ml-1 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border border-emerald-300/60 dark:border-emerald-800/60"
              title={`All amounts deflated by CPI YoY ${cpiYoy.toFixed(2)}%/yr, month-by-month. See nominal totals in the tooltip of each cell.`}
              data-testid="heatmap-real-badge"
            >
              today&#39;s $
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            <span>Avg ${avgAmount?.toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo</span>
            <span className="hidden sm:inline">·</span>
            <span>Total ${totalAnnual?.toLocaleString(undefined, { maximumFractionDigits: 0 })}/yr</span>
          </div>
          {/* Round 96n (Jul 3 2026) — Real vs Nominal toggle for the
              heatmap. Same pill grammar as StockTable + Screener so
              users only ever have to learn the pattern once. */}
          <div className="flex items-center gap-1.5" data-testid="heatmap-projection-mode-toggle">
            <div className="flex items-center bg-muted rounded-md p-0.5 border border-border/60" role="tablist" aria-label="Income display mode">
              <button
                type="button"
                onClick={() => setProjectionMode('nominal')}
                className={`px-2 py-0.5 text-[10px] font-semibold rounded transition-colors ${effectiveMode === 'nominal' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                title="Show nominal future dollars — raw projected payment per month, no inflation adjustment."
                data-testid="heatmap-proj-nominal"
              >
                Nominal
              </button>
              <button
                type="button"
                onClick={() => setProjectionMode('real')}
                disabled={typeof cpiYoy !== 'number'}
                className={`px-2 py-0.5 text-[10px] font-semibold rounded transition-colors ${effectiveMode === 'real' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'} disabled:opacity-40 disabled:cursor-not-allowed`}
                title={
                  typeof cpiYoy === 'number'
                    ? `Show every month in TODAY'S dollars — each month is discounted by (1 + ${cpiYoy.toFixed(2)}%)^(months-away/12) so Month 1 is barely touched and Month 12 gets the full inflation haircut.`
                    : "Real-dollar view unavailable — CPI YoY still loading."
                }
                data-testid="heatmap-proj-real"
              >
                Today&#39;s $
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-6 sm:grid-cols-12 gap-1.5">
        {months.map((m) => {
          const intensity = m.intensity_pct || 0;
          // Background opacity based on intensity (0..100 → 0.10..0.85)
          const bgOpacity = m.amount > 0 ? Math.max(0.10, Math.min(0.85, 0.10 + (intensity / 100) * 0.75)) : 0.04;
          const isHovered = hovered === m.key;
          // Round 84k Phase 5 — show the year alongside the month
          // label so users can distinguish e.g. Feb '26 from Feb '27.
          const yearSuffix = `'${String(m.year).slice(-2)}`;
          return (
            <div
              key={m.key}
              onMouseEnter={() => setHovered(m.key)}
              onMouseLeave={() => setHovered(null)}
              className={`relative rounded-md p-2 cursor-default border transition-all ${
                m.is_weak && m.amount > 0
                  ? 'border-amber-300/60 dark:border-amber-600/60'
                  : m.is_strong
                    ? 'border-emerald-400/70 dark:border-emerald-500/70'
                    : 'border-border/40'
              }`}
              style={{ backgroundColor: `rgba(139, 92, 246, ${bgOpacity})` }}
              data-testid={`heatmap-month-${m.key}`}
              title={
                useReal
                  ? `${MONTHS_SHORT[m.month - 1]} ${m.year} — $${m.amount.toLocaleString(undefined, { maximumFractionDigits: 0 })} in today's $ (nominal $${m._nominalAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}) from ${m.payment_count} payment${m.payment_count === 1 ? '' : 's'}`
                  : `${MONTHS_SHORT[m.month - 1]} ${m.year} — $${m.amount.toLocaleString()} from ${m.payment_count} payment${m.payment_count === 1 ? '' : 's'}`
              }
            >
              <div className="text-[10px] font-bold text-foreground/80 uppercase tracking-wide flex items-baseline gap-0.5">
                <span>{MONTHS_SHORT[m.month - 1]}</span>
                <span className="text-[9px] opacity-70">{yearSuffix}</span>
              </div>
              <div className="text-xs font-mono font-bold mt-0.5">
                ${m.amount >= 1000 ? `${(m.amount / 1000).toFixed(1)}k` : m.amount.toFixed(0)}
              </div>
              {m.payment_count > 0 && (
                <div className="text-[9px] text-muted-foreground mt-0.5">
                  {m.payment_count}x
                </div>
              )}
              {isHovered && m.tickers?.length > 0 && (
                <div className="absolute z-30 left-0 top-full mt-1 w-48 rounded-md border bg-popover shadow-lg p-2 text-[10px]">
                  <div className="font-bold mb-1">Payers ({m.tickers.length})</div>
                  <div className="text-muted-foreground break-words">{m.tickers.slice(0, 12).join(', ')}{m.tickers.length > 12 ? '…' : ''}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {(weakCount > 0 || strongCount > 0) && (
        <div className="flex items-center gap-3 mt-3 text-[11px] flex-wrap">
          {strongCount > 0 && (
            <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
              <TrendingUp className="w-3 h-3" />
              {strongCount} strong month{strongCount === 1 ? '' : 's'}
            </span>
          )}
          {weakCount > 0 && (
            <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400" data-testid="heatmap-weak-hint">
              <AlertCircle className="w-3 h-3" />
              {weakCount} weak month{weakCount === 1 ? '' : 's'} — consider adding income for these
            </span>
          )}
        </div>
      )}

      {/* "See Calendar for more →  Calendar" link — May 2026: relocated
          from the page-wide action bar into this box (left side, under
          the heatmap cells) per user request. Click navigates to the
          full payout calendar view. */}
      {onCalendarClick && (
        <div className="mt-3 flex items-center justify-start">
          <button
            type="button"
            onClick={onCalendarClick}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[16px] font-semibold border border-border bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors group"
            data-testid="heatmap-calendar-link"
            title="Open the full payout calendar"
          >
            <span>See Calendar for more</span>
            <ArrowRight className="w-[18px] h-[18px] transition-transform group-hover:translate-x-0.5" />
            <CalendarDays className="w-[18px] h-[18px] text-emerald-500 dark:text-emerald-400" />
            <span className="text-emerald-700 dark:text-emerald-300">Calendar</span>
          </button>
        </div>
      )}
    </div>
  );
}
